import orderBy from 'lodash.orderby';

import { strToAgreementStatus } from '@/_schemas/agreements/agreement-status-schema';
import { strToPipeline } from '@/_schemas/agreements/agreements-pipeline-schemas';
import { strToSubsidiesType } from '@/_schemas/agreements/subsidies-type-schemas';
import {
  booleanQueryToFormConverter,
  currencyInput,
  nullableNumericToNumericStringConverter,
} from '@/_schemas/generic';
import { dateTime } from '@/_shared';

import { strToContractingParty } from '../components/contracting-party/schema';
import { deliverableQueryToForm } from '../deliverables/helper';
import { installmentQueryToForm } from '../installments/helpers';
import type { AgreementFormSchema, AgreementQuerySchema } from '../schemas';

export function agreementQueryToForm(data: AgreementQuerySchema): AgreementFormSchema {
  const resolvedDeliverables = deliverableQueryToForm(data?.agreement_deliverables);
  return {
    ...data,
    status: strToAgreementStatus(data.status),
    pipeline: strToPipeline(data?.pipeline, !!data.parent_id),
    account_manager: orderBy(data?.account_manager ?? [], 'sort_order'),
    agreement_date: data.agreement_date ?? '',
    expiry_date: data.expiry_date ?? '',
    emergency_contact: data?.emergency_contact ?? [],
    agreement_deliverables: [...resolvedDeliverables],
    tempAgreement_deliverables: [...resolvedDeliverables],
    purchase_order: data.purchase_order ?? '',
    primary_domains: data.primary_domains?.[0] ?? null,
    agreement_contact: data.agreement_contact?.[0] ?? null,
    project_contact: data.project_contact?.[0] ?? null,
    billing_contact: data.billing_contact?.[0] ?? null,

    harvest: nullableNumericToNumericStringConverter(data.harvest),

    execution_date: dateQueryToForm(data?.execution_date),
    implementation_start_date: dateQueryToForm(data?.implementation_start_date),
    go_live_date: dateQueryToForm(data?.go_live_date),
    monthly_recurring_start_date: dateQueryToForm(data?.monthly_recurring_start_date),
    monthly_recurring_stop_date: dateQueryToForm(data?.monthly_recurring_stop_date),
    annual_recurring_start_date: dateQueryToForm(data?.annual_recurring_start_date),
    annual_recurring_stop_date: dateQueryToForm(data?.annual_recurring_stop_date),

    agreement_installments: data?.agreement_installments?.map(installmentQueryToForm) ?? [],
    wholesale_price_enabled: booleanQueryToFormConverter(data.wholesale_price_enabled),

    // !important static data
    selectedWholesaleDateRange: null,
    addresses: data?.addresses ?? [],
    addendums: orderBy(data?.addendums ?? [], 'agreement_id', 'desc'),

    contracting_party: strToContractingParty(data?.contracting_party),

    agreement_commissions:
      data.agreement_commissions?.map((c) => ({
        ...c,
        one_time_paid_amount: currencyInput.queryToFormConverter(c.one_time_paid_amount),
        recurring_paid_amount: currencyInput.queryToFormConverter(c.recurring_paid_amount),
      })) || [],
    activate_subsidy_billing: booleanQueryToFormConverter(Boolean(data.activate_subsidy_billing)),

    subsidies:
      data?.subsidies?.map((s) => ({
        ...s,
        addresses: s?.addresses ?? [],
        subsidy_type: strToSubsidiesType(s.subsidy_type),
        subsidy_deliverables: s.subsidy_deliverables?.map((d) => ({
          ...d,
          amount: currencyInput.queryToFormConverter(d.amount),
          percentage: currencyInput.queryToFormConverter(d.percentage),
          quantity: currencyInput.queryToFormConverter(d.quantity),
        })),
      })) ?? null,

    orphan_invoicing: booleanQueryToFormConverter(data.orphan_invoicing),
  };
}

function dateQueryToForm(data: string | undefined | null): string | undefined {
  return data ? dateTime.format(data, 'YYYY-MM-DD') : undefined;
}
